/* eslint-disable react/react-in-jsx-scope */
import CardInfos from "@/components/evolMap/components/CardInfos";
import MapLoader from "@/components/evolMap/components/MapLoader";
import MapSidebar from "@/components/evolMap/components/MapSidebar";
import "@/components/evolMap/components/MapViewer.css";
import { useDisplayParcels } from "@/components/evolMap/hooks/useDisplayParcels";
import { useFolderParcels } from "@/components/evolMap/hooks/useFolderParcels";
import useMap from "@/components/evolMap/hooks/useMap";
import { useMapViewerState } from "@/components/evolMap/hooks/useMapViewerState";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import L from "leaflet";
import drawLocales, { Language } from "leaflet-draw-locales";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface MapViewerProps {
  isReadOnly?: boolean;
  projectName: string;
  idProject: number;
}

const MapProject = ({
  isReadOnly = false,
  projectName,
  idProject,
}: MapViewerProps) => {
  const [status, setStatus] = useState<
    "close" | "add" | "read" | "edit" | "delete"
  >("close");
  const [selectedPolygon, setSelectedPolygon] = useState<L.Polygon | null>(
    null
  );
  console.log("idProject", idProject);
  const { map, mapRef, baseLayers } = useMap();
  const [currentParcel, setCurrentParcel] = useState(null);
  const currentFolderRef = useRef(null);

  const { data, loading } = useFetchDataV2(
    `${import.meta.env.VITE_API_ENDPOINT}/folders/${idProject}`,
    `folders-${idProject}`,
    1000 * 60 * 60
  );

  const { currentFolder, setCurrentFolder, handleAddFolder } =
    useFolderParcels(idProject);

  const { polygonMapRef, markerMapRef } = useDisplayParcels(
    map,
    data as any,
    (parcel, polygon, isCurrentPolygon) => {
      if (!isCurrentPolygon) {
        setCurrentParcel(parcel);
      }
      setStatus("read");
      setSelectedPolygon(polygon);
    },
    currentParcel,
    false,
    true
  );

  const [isOpen, setIsOpen] = useState(false);
  const { isMapLoaded } = useMapViewerState();

  const { i18n } = useTranslation();

  i18n.on("languageChanged", () => {
    drawLocales(i18n.language as Language);
  });

  const handleChangeCurrentFolder = useCallback(
    (folder) => {
      setStatus("close");
      setCurrentParcel(null);
      setCurrentFolder(folder);
    },
    [setCurrentFolder]
  );

  // ✅ handleChange corrigé
  const handleChange = (
    parcel,
    status: "close" | "add" | "read" | "edit" | "delete" = "read"
  ) => {
    setCurrentParcel({
      ...parcel,
      ...(parcel?.codeArr ? { type: 1 } : { type: 2 }),
    });

    const matchingPolygon = polygonMapRef.current.get(parcel.id) ?? null;

    handleMove(parcel.coordinates, parcel.id);

    if (status === "edit") {
      setStatus(matchingPolygon ? "edit" : "read");
    } else {
      setStatus(status);
    }

    setSelectedPolygon(matchingPolygon);
  };

  // ✅ handleMove corrigé
  const handleMove = (coordinates, parcelId?: number) => {
    let polygon = parcelId ? polygonMapRef.current.get(parcelId) : null;

    if (!polygon) {
      const normalizedCoords = coordinates.map(([lng, lat]) => [lat, lng]);
      polygon = L.polygon(normalizedCoords);
    }

    if (polygon) {
      map?.fitBounds(polygon.getBounds(), {
        padding: [20, 20],
        animate: true,
      });
    }
  };

  // ✅ Zoom auto au premier rendu
  const [hasZoomed, setHasZoomed] = useState(false);

  useEffect(() => {
    if (map && data && !hasZoomed) {
      const polygons = Array.from(polygonMapRef.current.values());

      if (polygons.length > 0) {
        const group = L.featureGroup(polygons);
        map.fitBounds(group.getBounds(), {
          padding: [20, 20],
          animate: true,
        });
        setHasZoomed(true);
      }
    }
  }, [map, data, polygonMapRef, hasZoomed]);

  // Mettre à jour la ref à chaque changement :
  useEffect(() => {
    currentFolderRef.current = currentFolder;
  }, [currentFolder]);

  if (!idProject) return <div>Id du projet invalide</div>;

  return (
    <div>
      <div id="map" className={`bg-white relative`}>
        <div className="relative z-30 overflow-hidden">
          <div
            ref={mapRef}
            style={{
              height: "600px",
              width: "100%",
            }}
          />

          {!isMapLoaded && <MapLoader isFullscreen={false} />}

          <MapSidebar
            isOpen={isOpen}
            onToggle={() => setIsOpen((v) => !v)}
            projectId={idProject}
            currentFolder={currentFolder}
            onAddFolder={handleAddFolder}
            onFolderSelect={handleChangeCurrentFolder}
            isReadOnly={true}
            currentParcel={currentParcel}
            handleChange={handleChange}
            handleMove={handleMove}
            setIsOpen={setIsOpen}
            data={data as any}
            loading={loading}
            baseLayers={baseLayers}
            map={map}
            removePolygon={(id) => console.log(id)}
            polygonMapRef={polygonMapRef}
            folders={undefined}
            setFolders={undefined}
          />
        </div>

        {status !== "close" && (
          <div className="absolute top-[4.5rem] right-2 z-[9999]">
            <CardInfos
              map={map}
              status={status}
              setStatus={setStatus}
              setParcel={setCurrentParcel}
              parcel={currentParcel}
              isReadOnly={isReadOnly}
              idProject={idProject}
              selectedPolygon={selectedPolygon}
              setSelectedPolygon={setSelectedPolygon}
              folders={data}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapProject;
